import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Circle,
  LayoutGrid,
  Loader2,
  Mic,
  PenLine,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  Users,
  Wand2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/formatApiError';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import {
  CAST_STAGES,
  CAST_STAGE_LABELS,
  CHARACTER_GENDERS,
  CHARACTER_ROLES,
  VOICE_PROVIDER_LABELS,
  addCastCharacter,
  fillCastProfiles,
  assignCastVoices,
  castMemberVoiced,
  confirmCastCharacters,
  castVoiceSlotId,
  fetchCreationVoices,
  generateCastPortrait,
  refreshCast,
  removeCastCharacter,
  updateCastCharacter,
  uploadCastPhoto,
  voicePickFromVoice,
  voicesForMember,
} from '@/lib/creationPlan';
import { VoiceBrowser, VoiceRow, useVoiceSamples } from '@/components/createPlan/VoicePickerQuestion';

import { DesignedVoicePreviews } from '@/components/createPlan/DesignedVoicePreviews';
import { TwinneeLogo } from '@/components/TwinneeLogo';
const PORTRAIT_CONCURRENCY = 2;
const SUGGESTIONS_PER_CHARACTER = 3;
const NARRATOR_KEY = 'narrator';
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

const label = (s) => String(s || '').replace(/_/g, ' ');

export function CastAvatar({ member, className = 'w-10 h-10' }) {
  const [failed, setFailed] = useState(false);
  const src = member?.image_url ? mediaUrlForPlayback(member.image_url) : '';
  useEffect(() => setFailed(false), [src]);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={member.name}
        onError={() => setFailed(true)}
        className={`${className} rounded-xl object-cover bg-white/5 shrink-0`}
      />
    );
  }
  return (
    <span
      className={`${className} rounded-xl shrink-0 bg-gradient-to-br from-[#2DD4BF]/25 to-[#7DD3FC]/15 border border-white/10 flex items-center justify-center text-sm font-bold text-[#5EEAD4]`}
    >
      {(member?.name || '?').slice(0, 1).toUpperCase()}
    </span>
  );
}

function Modal({ title, subtitle, onClose, children, footer, testId, wide = false }) {
  return createPortal(
    <div className="fixed inset-0 z-[205] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        data-testid={testId}
        className={`w-full ${wide ? 'max-w-4xl' : 'max-w-2xl'} max-h-[100dvh] sm:max-h-[90vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[#0b0e14] shadow-2xl`}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-white/10">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">{title}</p>
            {subtitle ? <p className="text-[11px] text-[#9CA3AF]">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-white hover:bg-white/10 cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
        {footer ? <div className="px-5 py-3 border-t border-white/10 flex flex-wrap items-center gap-2">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

async function runLimited(items, limit, fn) {
  const queue = [...items];
  const workers = Array.from({ length: Math.min(limit, queue.length) }, async () => {
    while (queue.length) {
      const item = queue.shift();
      // eslint-disable-next-line no-await-in-loop
      await fn(item);
    }
  });
  await Promise.all(workers);
}

/**
 * AI-build the cast: a portrait for every character still without a look, then finish the step.
 * A failed portrait doesn't stop the others; the character keeps no look and can be redone later.
 */
export async function autoBuildCast(planId, members, { onPlan, onPortraitError } = {}) {
  const missing = (members || []).filter((m) => !m.image_url);
  let failed = 0;
  await runLimited(missing, PORTRAIT_CONCURRENCY, async (member) => {
    try {
      onPlan?.(await generateCastPortrait(planId, member.id));
    } catch (err) {
      failed += 1;
      onPortraitError?.(member, err);
    }
  });
  if (missing.length) onPlan?.(await refreshCast(planId));
  const plan = await confirmCastCharacters(planId);
  onPlan?.(plan);
  return { plan, generated: missing.length - failed, failed };
}

function memberForm(member) {
  return {
    name: member?.name || '',
    role: member?.role || 'friend',
    gender: member?.gender || 'not_specified',
    look: member?.look || '',
    description: member?.description || '',
    traits: (member?.traits || []).map(label).join(', '),
  };
}

function ChipGroup({ title, value, options, onChange, testId }) {
  return (
    <div className="space-y-1" role="radiogroup" aria-label={title} data-testid={testId}>
      <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">{title}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={value === opt.id}
            onClick={() => onChange(opt.id)}
            className={`px-2.5 py-1 rounded-full text-[11px] capitalize cursor-pointer border transition-colors ${
              value === opt.id
                ? 'border-[#2DD4BF]/70 bg-[#2DD4BF]/15 text-[#2DD4BF]'
                : 'border-white/15 text-[#E5E7EB] hover:border-[#2DD4BF]/50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const inputClass =
  'w-full rounded-xl bg-white/[0.04] border border-white/15 px-3 py-2 text-xs text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#2DD4BF]/60';

/**
 * Character builder popup: every detected character's look (AI portrait or uploaded photo), role, gender,
 * description and traits. "Let Twinnee build all" generates the missing portraits and finishes the step.
 */
export function CastCharacterBuilderDialog({
  plan,
  focusId,
  onPlan,
  onClose,
  onDone,
  executed = false,
  restarting = false,
  onRestartStory,
}) {
  const members = useMemo(() => plan?.cast?.characters || [], [plan?.cast?.characters]);
  const [selectedId, setSelectedId] = useState(focusId || members[0]?.id || null);
  const selected = members.find((m) => m.id === selectedId) || members[0] || null;
  const [form, setForm] = useState(() => memberForm(selected));
  const [saving, setSaving] = useState(false);
  const [portraitBusy, setPortraitBusy] = useState({});
  const [building, setBuilding] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const fileRef = useRef(null);
  const planIdRef = useRef(plan?.id);
  planIdRef.current = plan?.id;

  const traitsKey = Array.isArray(selected?.traits) ? selected.traits.join('\u0001') : '';
  useEffect(() => {
    setForm((current) => {
      const next = memberForm(selected);
      if (!selected || current.name !== next.name) return next;
      return {
        ...current,
        description: current.description || next.description,
        traits: current.traits || next.traits,
      };
    });
  }, [selected, selected?.id, selected?.description, traitsKey]);

  const profileFilled = useRef('');
  const onPlanRef = useRef(onPlan);
  onPlanRef.current = onPlan;
  useEffect(() => {
    if (!plan?.id || profileFilled.current === plan.id) return undefined;
    const missing = members.some(
      (member) => !String(member.description || '').trim() || !(member.traits || []).length,
    );
    if (!missing) return undefined;
    profileFilled.current = plan.id;
    let cancelled = false;
    Promise.resolve(fillCastProfiles(plan.id))
      .then((next) => {
        if (!cancelled && next) onPlanRef.current?.(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [plan?.id, members]);

  const baseline = memberForm(selected);
  const dirty = Boolean(selected) && Object.keys(form).some((k) => form[k] !== baseline[k]);
  const anyPortraitBusy = Object.values(portraitBusy).some(Boolean);

  const saveForm = async () => {
    if (!selected || !dirty) return true;
    if (!form.name.trim()) {
      toast.error('The character needs a name');
      return false;
    }
    setSaving(true);
    try {
      onPlan(await updateCastCharacter(planIdRef.current, selected.id, form));
      return true;
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save the character'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const generatePortrait = async (member) => {
    setPortraitBusy((b) => ({ ...b, [member.id]: true }));
    try {
      onPlan(await generateCastPortrait(planIdRef.current, member.id));
      return true;
    } catch (err) {
      toast.error(formatApiError(err, `Couldn't draw ${member.name}`));
      return false;
    } finally {
      setPortraitBusy((b) => ({ ...b, [member.id]: false }));
    }
  };

  const onPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selected) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      toast.error('Use a JPEG, PNG or WEBP image');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error('Image too large (max 10 MB)');
      return;
    }
    setPortraitBusy((b) => ({ ...b, [selected.id]: true }));
    try {
      onPlan(await uploadCastPhoto(planIdRef.current, selected.id, file));
    } catch (err) {
      toast.error(formatApiError(err, 'Photo upload failed'));
    } finally {
      setPortraitBusy((b) => ({ ...b, [selected.id]: false }));
    }
  };

  const addCharacter = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      const next = await addCastCharacter(planIdRef.current, { name });
      onPlan(next);
      const added = (next.cast?.characters || []).at(-1);
      if (added) setSelectedId(added.id);
      setNewName('');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not add the character'));
    } finally {
      setAdding(false);
    }
  };

  const removeSelected = async () => {
    if (!selected) return;
    try {
      const next = await removeCastCharacter(planIdRef.current, selected.id);
      onPlan(next);
      setSelectedId(next.cast?.characters?.[0]?.id || null);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not remove the character'));
    }
  };

  const finish = async () => {
    if (!(await saveForm())) return;
    setFinishing(true);
    try {
      const next = await confirmCastCharacters(planIdRef.current);
      onPlan(next);
      onDone?.(next, { auto: false });
    } catch (err) {
      toast.error(formatApiError(err, 'Could not finish the characters'));
    } finally {
      setFinishing(false);
    }
  };

  const buildAll = async () => {
    if (!(await saveForm())) return;
    setBuilding(true);
    const missing = members.filter((m) => !m.image_url);
    setPortraitBusy(Object.fromEntries(missing.map((m) => [m.id, true])));
    try {
      const { plan: next, failed } = await autoBuildCast(planIdRef.current, members, {
        onPlan,
        onPortraitError: (member, err) => toast.error(formatApiError(err, `Couldn't draw ${member.name}`)),
      });
      onDone?.(next, { auto: true, failed });
    } catch (err) {
      toast.error(formatApiError(err, 'Could not build the characters'));
    } finally {
      setBuilding(false);
      setPortraitBusy({});
    }
  };

  const busy = saving || building || finishing;
  const withPortrait = members.filter((m) => m.image_url).length;

  return (
    <Modal
      title="Build your characters"
      subtitle={`${members.length} character${members.length === 1 ? '' : 's'} from your story · ${withPortrait} with a look · portrait, traits, and an optional designed voice for each`}
      onClose={onClose}
      testId="cast-builder-dialog"
      wide
      footer={
        <>
          {executed ? null : (
            <button
              type="button"
              onClick={buildAll}
              disabled={busy || anyPortraitBusy || !members.length}
              data-testid="cast-builder-auto"
              className="px-3.5 py-2 rounded-xl border border-dashed border-[#2DD4BF]/50 text-[#5EEAD4] text-xs font-semibold cursor-pointer flex items-center gap-1.5 hover:bg-[#2DD4BF]/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {building ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TwinneeLogo className="w-3.5 h-3.5" />}
              {building ? 'Building…' : 'Let Twinnee build all'}
            </button>
          )}
          <span className="text-[10px] text-[#6B7280] hidden sm:inline">
            {executed
              ? 'Portraits update the cast. Restart the story so every scene uses the new looks.'
              : 'AI draws a portrait for each character still without one'}
          </span>
          {executed && onRestartStory ? (
            <button
              type="button"
              onClick={onRestartStory}
              disabled={busy || anyPortraitBusy || restarting}
              data-testid="cast-builder-restart-story"
              className="ml-auto px-4 py-2 rounded-xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] text-xs font-bold cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {restarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {restarting ? 'Restarting…' : 'Restart story with these looks'}
            </button>
          ) : (
            <button
              type="button"
              onClick={executed ? onClose : finish}
              disabled={busy || anyPortraitBusy || (!executed && !members.length)}
              data-testid="cast-builder-done"
              className="ml-auto px-4 py-2 rounded-xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] text-xs font-bold cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {finishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {executed ? 'Done' : 'Done — continue to voices'}
            </button>
          )}
        </>
      }
    >
      <div className="flex flex-col md:flex-row min-h-[420px]">
        <div className="md:w-60 shrink-0 border-b md:border-b-0 md:border-r border-white/10 p-3 space-y-1.5">
          {members.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={async () => {
                if (m.id === selected?.id || (await saveForm())) setSelectedId(m.id);
              }}
              data-testid="cast-builder-member"
              className={`w-full flex items-center gap-2.5 rounded-xl px-2 py-1.5 text-left cursor-pointer border transition-colors ${
                m.id === selected?.id ? 'border-[#2DD4BF]/60 bg-[#2DD4BF]/10' : 'border-transparent hover:bg-white/5'
              }`}
            >
              <CastAvatar member={m} className="w-9 h-9" />
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-white truncate">{m.name}</span>
                <span className="block text-[10px] text-[#9CA3AF] capitalize truncate">
                  {label(m.role)}
                  {m.gender && m.gender !== 'not_specified' ? ` · ${label(m.gender)}` : ''}
                </span>
              </span>
              <span className="flex items-center gap-1 shrink-0">
                {m.voice?.voice_id ? (
                  <Mic className="w-3 h-3 text-[#A78BFA]" aria-label="Has a voice" />
                ) : null}
                {portraitBusy[m.id] ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2DD4BF]" />
                ) : m.image_url ? (
                  <Check className="w-3.5 h-3.5 text-[#2DD4BF]" />
                ) : (
                  <Circle className="w-3 h-3 text-[#4B5563]" />
                )}
              </span>
            </button>
          ))}
          {executed ? null : (
            <div className="pt-2 flex gap-1.5">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addCharacter();
                }}
                placeholder="Add a character…"
                className={`${inputClass} py-1.5`}
                data-testid="cast-builder-new-name"
              />
              <button
                type="button"
                onClick={addCharacter}
                disabled={!newName.trim() || adding}
                aria-label="Add character"
                className="shrink-0 w-8 rounded-xl border border-white/15 text-[#E5E7EB] flex items-center justify-center cursor-pointer hover:border-[#2DD4BF]/50 disabled:opacity-40"
              >
                {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              </button>
            </div>
          )}
        </div>

        {selected ? (
          <div className="flex-1 p-4 sm:p-5 space-y-4" data-testid="cast-builder-editor">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="shrink-0 space-y-2">
                <div className="relative">
                  <CastAvatar member={selected} className="w-40 h-40" />
                  {portraitBusy[selected.id] ? (
                    <div className="absolute inset-0 rounded-xl bg-black/60 flex flex-col items-center justify-center gap-1 text-[10px] text-[#9CA3AF]">
                      <Loader2 className="w-5 h-5 animate-spin text-[#2DD4BF]" /> Creating look…
                    </div>
                  ) : null}
                </div>
                <div className="flex gap-1.5 w-40">
                  <button
                    type="button"
                    onClick={async () => {
                      if (await saveForm()) generatePortrait(selected);
                    }}
                    disabled={Boolean(portraitBusy[selected.id]) || building}
                    data-testid="cast-builder-portrait-ai"
                    className="flex-1 py-1.5 rounded-lg border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 text-[#5EEAD4] text-[10px] font-semibold cursor-pointer flex items-center justify-center gap-1 hover:bg-[#2DD4BF]/20 disabled:opacity-50"
                  >
                    <Wand2 className="w-3 h-3" /> {selected.image_url ? 'Redraw' : 'AI portrait'}
                  </button>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={Boolean(portraitBusy[selected.id]) || building}
                    className="flex-1 py-1.5 rounded-lg border border-white/15 text-[#E5E7EB] text-[10px] font-semibold cursor-pointer flex items-center justify-center gap-1 hover:bg-white/5 disabled:opacity-50"
                  >
                    <Upload className="w-3 h-3" /> Photo
                  </button>
                  <input ref={fileRef} type="file" accept={PHOTO_TYPES.join(',')} className="hidden" onChange={onPhoto} />
                </div>
                {selected.portrait_source ? (
                  <p className="text-[10px] text-[#6B7280] w-40">
                    {selected.portrait_source === 'ai' ? 'AI portrait' : 'Uploaded photo'} · used as the reference in every scene
                  </p>
                ) : null}
              </div>

              <div className="flex-1 min-w-0 space-y-3">
                <label className="block space-y-1">
                  <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Name</span>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className={inputClass}
                    data-testid="cast-builder-name"
                  />
                </label>
                <ChipGroup
                  title="Role"
                  value={form.role}
                  options={CHARACTER_ROLES.map((r) => ({ id: r, label: r }))}
                  onChange={(role) => setForm((f) => ({ ...f, role }))}
                  testId="cast-builder-role"
                />
                <ChipGroup
                  title="Gender"
                  value={form.gender}
                  options={CHARACTER_GENDERS}
                  onChange={(gender) => setForm((f) => ({ ...f, gender }))}
                  testId="cast-builder-gender"
                />
                <label className="block space-y-1">
                  <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Look</span>
                  <textarea
                    rows={3}
                    maxLength={1200}
                    value={form.look}
                    onChange={(e) => setForm((f) => ({ ...f, look: e.target.value }))}
                    placeholder="Physical appearance only — species or age, build, face, hair, eyes, clothing, accessories"
                    className={`${inputClass} resize-none`}
                    data-testid="cast-builder-look"
                  />
                  <span className="block text-[10px] text-[#6B7280]">
                    Used to draw the portrait and keep {form.name || 'them'} looking the same in every scene.
                  </span>
                </label>
                <label className="block space-y-1">
                  <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Description</span>
                  <textarea
                    rows={3}
                    maxLength={2000}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Personality, backstory, and how they relate to the other characters"
                    className={`${inputClass} resize-none`}
                    data-testid="cast-builder-description"
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Personality traits</span>
                  <input
                    value={form.traits}
                    onChange={(e) => setForm((f) => ({ ...f, traits: e.target.value }))}
                    placeholder="brave, curious, a little clumsy"
                    className={inputClass}
                    data-testid="cast-builder-traits"
                  />
                </label>
              </div>
            </div>

            <div
              className="rounded-xl border border-[#A78BFA]/25 bg-[#A78BFA]/5 p-3 space-y-2"
              data-testid="cast-builder-voice-design"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold text-[#E9D5FF] flex items-center gap-1.5">
                  <Mic className="w-3.5 h-3.5 text-[#A78BFA]" />
                  Voice for {form.name || selected.name || 'this character'}
                </p>
                {selected.voice?.voice_id ? (
                  <span
                    className="text-[10px] text-[#5EEAD4] flex items-center gap-1 min-w-0"
                    data-testid="cast-builder-voice-current"
                    title={selected.voice.name || selected.voice.voice_id}
                  >
                    <Check className="w-3 h-3 shrink-0" />
                    <span className="truncate max-w-[180px]">{selected.voice.name || selected.voice.voice_id}</span>
                  </span>
                ) : (
                  <span className="text-[10px] text-amber-300/90">No voice yet — design one below</span>
                )}
              </div>
              <DesignedVoicePreviews
                key={`cast-design-${selected.id}`}
                planId={plan.id}
                slot={castVoiceSlotId(selected.id)}
                title={`Design a custom voice for ${form.name || selected.name}`}
                compact
                defaultGender={
                  form.gender && form.gender !== 'not_specified'
                    ? form.gender
                    : selected.gender && selected.gender !== 'not_specified'
                      ? selected.gender
                      : ''
                }
                onCommitted={(voice, nextPlan) => {
                  if (nextPlan) onPlan(nextPlan);
                  toast.success(
                    voice?.name
                      ? `Voice “${voice.name}” saved for ${form.name || selected.name}`
                      : `Voice saved for ${form.name || selected.name}`,
                  );
                }}
              />
            </div>

            <div className="flex items-center gap-2">
              {executed ? null : (
                <button
                  type="button"
                  onClick={removeSelected}
                  className="text-[11px] text-[#9CA3AF] hover:text-[#FCA5A5] cursor-pointer flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Remove from this story
                </button>
              )}
              <button
                type="button"
                onClick={saveForm}
                disabled={!dirty || saving}
                data-testid="cast-builder-save"
                className="ml-auto px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer bg-white/10 text-white border border-white/15 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {dirty ? 'Save changes' : 'Saved'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 p-8 text-center">
            <Users className="w-6 h-6 text-[#4B5563]" />
            <p className="text-xs text-[#9CA3AF] max-w-xs">
              No characters were found in the story. Add them on the left — name each person or creature who speaks.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

function voiceKey(pick) {
  return pick?.voice_id ? `${pick.provider}:${pick.voice_id}` : '';
}

/**
 * Voice allocation popup: a voice per character (and the narrator when the story has one), picked from
 * suggestions matched to each character, the full catalog, or allocated automatically.
 */
export function CastVoiceDialog({ plan, onPlan, onClose, onDone }) {
  const members = useMemo(() => plan?.cast?.characters || [], [plan?.cast?.characters]);
  const narratorNeeded = Boolean(plan?.cast_status?.narrator_needed);
  const [catalog, setCatalog] = useState({ loading: true, error: '', voices: [], language: 'en' });
  const [draft, setDraft] = useState(() => {
    const out = {};
    members.forEach((m) => {
      if (m.voice?.voice_id) out[m.id] = m.voice;
    });
    const narrator = plan?.cast?.narrator?.voice;
    if (narrator?.voice_id) out[NARRATOR_KEY] = narrator;
    return out;
  });
  const [browserFor, setBrowserFor] = useState(null);
  const [saving, setSaving] = useState('');
  const samples = useVoiceSamples(catalog.language);

  const load = async () => {
    setCatalog((c) => ({ ...c, loading: true, error: '' }));
    try {
      const data = await fetchCreationVoices(plan.id);
      setCatalog({ loading: false, error: '', voices: data.voices, language: data.language });
    } catch (err) {
      setCatalog((c) => ({ ...c, loading: false, error: formatApiError(err, 'Could not load voices') }));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.id]);

  const rows = [
    ...members.map((m) => ({ key: m.id, member: m, title: m.name })),
    ...(narratorNeeded
      ? [{ key: NARRATOR_KEY, member: { name: 'Narrator', role: 'narrator', gender: '' }, title: 'Narrator' }]
      : []),
  ];
  const missing = rows.filter((r) => !draft[r.key]?.voice_id).length;

  const assign = (rowKey, voice) => {
    samples.stop();
    setBrowserFor(null);
    setDraft((d) => ({ ...d, [rowKey]: voicePickFromVoice(voice) }));
  };

  const changedAssignments = () => {
    const assignments = {};
    members.forEach((m) => {
      if (draft[m.id]?.voice_id && voiceKey(draft[m.id]) !== voiceKey(m.voice)) assignments[m.id] = draft[m.id];
    });
    const narrator =
      draft[NARRATOR_KEY]?.voice_id && voiceKey(draft[NARRATOR_KEY]) !== voiceKey(plan?.cast?.narrator?.voice)
        ? draft[NARRATOR_KEY]
        : null;
    return { assignments, narrator };
  };

  const submit = async ({ auto }) => {
    samples.stop();
    setSaving(auto ? 'auto' : 'save');
    try {
      const next = await assignCastVoices(plan.id, { ...changedAssignments(), auto, confirm: true });
      onPlan(next);
      const voiced = next.cast_status?.stage !== 'voices';
      if (voiced) {
        onDone?.(next, { auto });
      } else {
        const refreshed = {};
        (next.cast?.characters || []).forEach((m) => {
          if (m.voice?.voice_id) refreshed[m.id] = m.voice;
        });
        if (next.cast?.narrator?.voice?.voice_id) refreshed[NARRATOR_KEY] = next.cast.narrator.voice;
        setDraft((d) => ({ ...d, ...refreshed }));
        toast.info('Some characters still need a voice');
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save the voices'));
    } finally {
      setSaving('');
    }
  };

  const takenBy = (rowKey) =>
    new Set(Object.entries(draft).filter(([k, v]) => k !== rowKey && v?.voice_id).map(([, v]) => voiceKey(v)));

  const browserRow = rows.find((r) => r.key === browserFor);

  return (
    <Modal
      title="Allocate voices"
      subtitle={`Give every ${narratorNeeded ? 'character and the narrator' : 'character'} a voice · play samples, choose, or let Twinnee match them`}
      onClose={onClose}
      testId="cast-voice-dialog"
      wide
      footer={
        <>
          <button
            type="button"
            onClick={() => submit({ auto: true })}
            disabled={Boolean(saving) || catalog.loading}
            data-testid="cast-voices-auto"
            className="px-3.5 py-2 rounded-xl border border-dashed border-[#2DD4BF]/50 text-[#5EEAD4] text-xs font-semibold cursor-pointer flex items-center gap-1.5 hover:bg-[#2DD4BF]/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving === 'auto' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TwinneeLogo className="w-3.5 h-3.5" />}
            {missing ? `Auto-assign the remaining ${missing}` : 'Auto-assign'}
          </button>
          <span className="text-[10px] text-[#6B7280]">{missing ? `${missing} still without a voice` : 'Everyone has a voice'}</span>
          <button
            type="button"
            onClick={() => submit({ auto: false })}
            disabled={Boolean(saving) || missing > 0}
            data-testid="cast-voices-save"
            className="ml-auto px-4 py-2 rounded-xl bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] text-xs font-bold cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Save voices
          </button>
        </>
      }
    >
      <div className="p-4 sm:p-5 space-y-3">
        {catalog.loading ? (
          <p className="text-[11px] text-[#9CA3AF] flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Finding voices that fit your characters…
          </p>
        ) : catalog.error || !catalog.voices.length ? (
          <div className="flex items-center gap-2 text-[11px] text-[#9CA3AF]">
            {catalog.error || 'No character voices are configured on this server.'}
            <button type="button" onClick={load} className="text-[#5EEAD4] hover:underline cursor-pointer flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Retry
            </button>
          </div>
        ) : null}
        {rows.map((row) => {
          const current = draft[row.key];
          const taken = takenBy(row.key);
          const suggestions = voicesForMember(catalog.voices, row.member)
            .filter((v) => !taken.has(v.key) && v.key !== voiceKey(current))
            .slice(0, SUGGESTIONS_PER_CHARACTER);
          return (
            <div
              key={row.key}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 space-y-2"
              data-testid="cast-voice-row"
            >
              <div className="flex items-center gap-3">
                {row.key === NARRATOR_KEY ? (
                  <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <Mic className="w-4 h-4 text-[#5EEAD4]" />
                  </span>
                ) : (
                  <CastAvatar member={row.member} />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-white truncate">{row.title}</p>
                  <p className="text-[10px] text-[#9CA3AF] capitalize truncate">
                    {label(row.member.role)}
                    {row.member.gender && row.member.gender !== 'not_specified' ? ` · ${label(row.member.gender)}` : ''}
                  </p>
                </div>
                {current?.voice_id ? (
                  <span className="text-[11px] text-[#5EEAD4] flex items-center gap-1 min-w-0" data-testid="cast-voice-current">
                    <Check className="w-3 h-3 shrink-0" />
                    <span className="truncate max-w-[160px]">{current.name || current.voice_id}</span>
                    <span className="text-[#6B7280] shrink-0">· {VOICE_PROVIDER_LABELS[current.provider] || current.provider}</span>
                  </span>
                ) : (
                  <span className="text-[11px] text-amber-300">No voice yet</span>
                )}
              </div>
              <DesignedVoicePreviews
                planId={plan.id}
                slot={castVoiceSlotId(row.key)}
                title={`Design a voice for ${row.title}`}
                compact
                defaultGender={
                  row.member?.gender && row.member.gender !== 'not_specified' ? row.member.gender : ''
                }
                onCommitted={(voice) =>
                  setDraft((d) => ({
                    ...d,
                    [row.key]: {
                      provider: voice.provider || 'elevenlabs',
                      voice_id: voice.voice_id,
                      name: voice.name || voice.voice_id,
                    },
                  }))
                }
              />
              {suggestions.length ? (
                <div className="grid gap-1.5 md:grid-cols-2">
                  {suggestions.map((v) => (
                    <VoiceRow key={v.key} voice={v} samples={samples} onPick={(voice) => assign(row.key, voice)} compact />
                  ))}
                </div>
              ) : null}
              {catalog.voices.length ? (
                <button
                  type="button"
                  onClick={() => setBrowserFor(row.key)}
                  className="px-3 py-1 rounded-full text-[11px] cursor-pointer border border-white/15 text-[#E5E7EB] hover:border-[#2DD4BF]/50 inline-flex items-center gap-1"
                >
                  <LayoutGrid className="w-3 h-3" /> More voices
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
      {browserRow ? (
        <VoiceBrowser
          voices={voicesForMember(catalog.voices, browserRow.member)}
          suggestedKeys={new Set()}
          samples={samples}
          onPick={(voice) => assign(browserRow.key, voice)}
          onClose={() => setBrowserFor(null)}
          prompt={`Voice for ${browserRow.title}`}
        />
      ) : null}
    </Modal>
  );
}

const STEP_ORDER = CAST_STAGES.slice(0, 3);

function stepState(step, stage, executed) {
  if (executed || stage === 'done') return 'done';
  const at = CAST_STAGES.indexOf(stage);
  const idx = CAST_STAGES.indexOf(step);
  if (idx < at) return 'done';
  return idx === at ? 'active' : 'pending';
}

/** Right-panel status of the dialogue cast: overview, characters with their looks, voices, generation. */
export function CastStatusPanel({
  plan,
  executed,
  busyStep,
  onOpenBuilder,
  onOpenVoices,
  onRegenerateOverview,
  onSaveOverview,
}) {
  const status = plan?.cast_status;
  const cast = plan?.cast;
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  if (!status || !cast) return null;
  const stage = status.stage;
  const members = cast.characters || [];
  const canEdit = !executed && !busyStep;

  const stepDetail = {
    overview: status.overview_ready
      ? cast.overview_source === 'script'
        ? 'From your script'
        : cast.overview_source === 'user'
          ? 'Edited by you'
          : 'Written by AI'
      : busyStep === 'overview'
        ? 'Writing…'
        : 'Not written yet',
    characters: members.length
      ? `${members.length} characters · ${status.with_portrait} with a look`
      : busyStep === 'detect'
        ? 'Detecting…'
        : 'Not detected yet',
    voices: `${status.with_voice}/${members.length} voiced${
      status.narrator_needed ? ` · narrator ${status.narrator_voiced ? 'voiced' : 'pending'}` : ''
    }`,
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#11151e]/95 p-4 space-y-4" data-testid="cast-status-panel">
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-[#2DD4BF]" />
        <p className="text-sm font-semibold text-white">Story cast</p>
        <span className="ml-auto text-[10px] font-mono text-[#9CA3AF]">
          {executed ? 'Story generating' : CAST_STAGE_LABELS[stage] || ''}
        </span>
      </div>

      <ol className="grid grid-cols-3 gap-2">
        {STEP_ORDER.map((step) => {
          const state = stepState(step, stage, executed);
          const busy =
            (step === 'overview' && busyStep === 'overview') ||
            (step === 'characters' && ['detect', 'auto-build'].includes(busyStep)) ||
            (step === 'voices' && busyStep === 'auto-voices');
          return (
            <li
              key={step}
              data-testid={`cast-step-${step}`}
              data-state={state}
              className={`rounded-xl border px-2.5 py-2 ${
                state === 'done'
                  ? 'border-[#2DD4BF]/30 bg-[#2DD4BF]/[0.06]'
                  : state === 'active'
                    ? 'border-amber-400/40 bg-amber-400/[0.06]'
                    : 'border-white/10 bg-white/[0.02]'
              }`}
            >
              <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] flex items-center gap-1">
                {busy ? (
                  <Loader2 className="w-2.5 h-2.5 animate-spin text-[#2DD4BF]" />
                ) : state === 'done' ? (
                  <Check className="w-2.5 h-2.5 text-[#2DD4BF]" />
                ) : (
                  <Circle className={`w-2.5 h-2.5 ${state === 'active' ? 'text-amber-300' : 'text-[#4B5563]'}`} />
                )}
                {CAST_STAGE_LABELS[step]}
              </p>
              <p className="text-[11px] text-white mt-0.5 truncate">{stepDetail[step]}</p>
            </li>
          );
        })}
      </ol>

      {status.overview_ready ? (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Story overview</p>
            {canEdit && !editing ? (
              <span className="ml-auto flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setText(cast.overview || '');
                    setEditing(true);
                  }}
                  className="text-[10px] text-[#9CA3AF] hover:text-white cursor-pointer flex items-center gap-1"
                >
                  <PenLine className="w-3 h-3" /> Edit
                </button>
                <button
                  type="button"
                  onClick={onRegenerateOverview}
                  className="text-[10px] text-[#9CA3AF] hover:text-white cursor-pointer flex items-center gap-1"
                  title="Write a new overview (the cast is detected again)"
                >
                  <RefreshCw className="w-3 h-3" /> Rewrite
                </button>
              </span>
            ) : null}
          </div>
          {editing ? (
            <div className="space-y-1.5">
              <textarea
                rows={8}
                value={text}
                onChange={(e) => setText(e.target.value)}
                className={`${inputClass} resize-y`}
                data-testid="cast-overview-editor"
              />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditing(false)} className="text-[11px] text-[#9CA3AF] cursor-pointer">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (await onSaveOverview(text)) setEditing(false);
                  }}
                  className="px-3 py-1 rounded-full text-[11px] font-semibold bg-[#2DD4BF] text-[#06080D] cursor-pointer"
                >
                  Save overview
                </button>
              </div>
            </div>
          ) : (
            <>
              <p
                className={`text-xs leading-relaxed text-[#cbd5e1] whitespace-pre-line ${expanded ? '' : 'line-clamp-5'}`}
                data-testid="cast-overview"
              >
                {cast.overview}
              </p>
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="text-[10px] text-[#5EEAD4] hover:underline cursor-pointer flex items-center gap-0.5"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expanded ? 'Show less' : 'Show full overview'}
              </button>
            </>
          )}
        </div>
      ) : null}

      {members.length ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Characters</p>
            <span className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => onOpenBuilder()}
                className="text-[10px] text-[#9CA3AF] hover:text-white cursor-pointer flex items-center gap-1"
                data-testid="cast-open-builder"
              >
                <Wand2 className="w-3 h-3" /> Character builder
              </button>
              {canEdit && (stage === 'voices' || stage === 'done') ? (
                <button
                  type="button"
                  onClick={onOpenVoices}
                  className="text-[10px] text-[#9CA3AF] hover:text-white cursor-pointer flex items-center gap-1"
                  data-testid="cast-open-voices"
                >
                  <Mic className="w-3 h-3" /> Voices
                </button>
              ) : null}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {members.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => onOpenBuilder(m.id)}
                data-testid="cast-member-card"
                className="flex gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] p-2 text-left cursor-pointer hover:border-[#2DD4BF]/40"
              >
                <CastAvatar member={m} className="w-14 h-14" />
                <span className="min-w-0 flex-1 space-y-0.5">
                  <span className="block text-xs font-semibold text-white truncate">{m.name}</span>
                  <span className="block text-[10px] text-[#9CA3AF] capitalize truncate">
                    {label(m.role)}
                    {m.gender && m.gender !== 'not_specified' ? ` · ${label(m.gender)}` : ''}
                  </span>
                  {m.traits?.length ? (
                    <span className="block text-[10px] text-[#6B7280] truncate">{m.traits.slice(0, 3).map(label).join(' · ')}</span>
                  ) : null}
                  <span
                    className={`flex items-center gap-1 text-[10px] truncate ${castMemberVoiced(m) ? 'text-[#5EEAD4]' : 'text-[#6B7280]'}`}
                  >
                    <Mic className="w-2.5 h-2.5 shrink-0" />
                    {castMemberVoiced(m) ? m.voice.name || m.voice.voice_id : 'No voice yet'}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {status.narrator_needed ? (
            <p className="text-[10px] text-[#9CA3AF] flex items-center gap-1">
              <Mic className="w-3 h-3" /> Narrator:{' '}
              {cast.narrator?.voice?.voice_id ? cast.narrator.voice.name || cast.narrator.voice.voice_id : 'no voice yet'}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
